import { useEffect, useRef } from 'react';
import { SCROLL_TO_HASH_MESSAGE_TYPE } from './ScrollToHashMessageType';

//Default parent scroll offset (in pixels) used by fallback window scrolling ; this offset keeps content visible below sticky headers
const PARENT_SCROLL_OFFSET_PX = 80;

/**
 * Scroll handler function called when a hash link is clicked inside EP
 *
 * @param iframe
 * @param offsetTop - The offset (in pixels) from the top of the iframe document to the target section
 */
export type ScrollToHashHandler = (iframe: HTMLIFrameElement, offsetTop: number) => void;

/**
 * Listen for scroll-to-hash messages from the embed and scroll the parent so the target section is in view
 *
 * @param isHeightSetFromContent
 * @param uniqueKey - Optional unique identifier for the embedded instance
 * @param customScrollHandler - Optional custom scroll handler for non-standard scroll containers (e.g. overflow: auto on a div)
 */
export const useScrollToHashFromEmbed = (
	isHeightSetFromContent: boolean | undefined,
	uniqueKey?: string,
	customScrollHandler?: ScrollToHashHandler,
): void => {
	const iframeBySourceRef = useRef<WeakMap<object, HTMLIFrameElement>>(new WeakMap());
	const customScrollHandlerRef = useRef<ScrollToHashHandler | undefined>(customScrollHandler);

	useEffect(() => {
		customScrollHandlerRef.current = customScrollHandler;
	}, [customScrollHandler]);

	useEffect(() => {
		if (!isHeightSetFromContent) {
			return undefined;
		}

		const handler = (event: MessageEvent) => {
			if (event.data?.type !== SCROLL_TO_HASH_MESSAGE_TYPE) {
				return;
			}

			// Only accept messages sent by the same embed instance
			if (uniqueKey && event.data?.uniqueKey !== uniqueKey) {
				return;
			}
			const offsetTop = event.data?.offsetTop;
			if (typeof offsetTop !== 'number') {
				return;
			}
			const source = event.source;
			if (!source || typeof source !== 'object') {
				return;
			}

			let iframe = iframeBySourceRef.current.get(source);
			if (!iframe) {
				// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
				iframe = Array.from(document.querySelectorAll('iframe')).find(
					(f) => f.contentWindow === source,
				);
				if (iframe) {
					iframeBySourceRef.current.set(source, iframe);
				}
			}

			if (!iframe) {
				return;
			}

			// Use custom handler if provided, otherwise fall back to default scroll
			if (customScrollHandlerRef.current) {
				customScrollHandlerRef.current(iframe, offsetTop);
			} else {
				const iframeRect = iframe.getBoundingClientRect();
				const scrollTarget = iframeRect.top + window.scrollY + offsetTop - PARENT_SCROLL_OFFSET_PX;
				window.scrollTo({
					top: Math.max(0, scrollTarget),
					behavior: 'smooth',
				});
			}
		};

		window.addEventListener('message', handler);
		return () => window.removeEventListener('message', handler);
	}, [isHeightSetFromContent, uniqueKey]);
};
